import { Plus } from '@crehana/compass.icons.outline/v2/plus';
import { Button } from '@crehana/compass.ui/v3/button';
import { Toaster, toast } from '@crehana/compass.ui/v3/toaster';
import {
  PageLayout,
  PageLayoutBreadcrumb,
  PageLayoutContent,
  PageLayoutTop,
} from '@crehana/compass.web/page-layout';
import { Helmet } from '@modern-js/runtime/head';
import { useState } from 'react';

import { PageHeaderWithActions } from '@/shared/components/page-header-with-actions';
import type { TRoutePathnames } from '@/shared/modern-router';

import {
  CreateRequestDialog,
  type TCreateRequestBannerCode,
  type TCreateRequestPayload,
} from './components/create-request-dialog';
import { RequestsTable } from './components/requests-table';
import { useCreateOvertimeRequest } from './hooks/use-create-overtime-request';
import { useMyRequests } from './hooks/use-my-requests';
import { useMyRequestsPagination } from './hooks/use-my-requests-pagination';
import { useAddI18nLocales, useTranslation } from './i18n';

export const UserAttendanceMyRequestsPage = () => {
  useAddI18nLocales();
  const { t } = useTranslation();

  const { page, pageSize, onPageChange, onPageSizeChange } =
    useMyRequestsPagination();
  const { items, totalCount, loading, error, refetch } = useMyRequests({
    page,
    pageSize,
  });
  const isEmpty = !loading && !error && items.length === 0;

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const { createRequest, creating } = useCreateOvertimeRequest();

  const handleCreateClick = () => setCreateDialogOpen(true);

  // `useTableState` seeds its page index once and never clamps it, so a
  // same-page refetch after the last row on page N > 1 cancels can return an
  // empty list and hide the real content behind the "no requests at all"
  // empty state. Reset to page 1 unconditionally: it is the simplest option
  // that always lands on a page the collaborator has (TIM-560 review finding
  // CRITICAL-2). A change to page 1 requests fresh data through
  // `useMyRequests`'s own variables; call `refetch` only when the page
  // was already 1, where that variable change would not happen.
  const handleRequestCancelled = () => {
    if (page === 1) {
      refetch();
      return;
    }
    onPageChange(1);
  };

  const handleCreateSubmit = async (
    payload: TCreateRequestPayload,
  ): Promise<TCreateRequestBannerCode | null> => {
    const status = await createRequest(payload);

    if (status === 'success') {
      setCreateDialogOpen(false);
      toast.success(t('TOAST_CREATE_SUCCESS'));
      refetch();
      return null;
    }
    if (status === 'duplicate' || status === 'shiftRequired') {
      return status;
    }
    toast.error(t('TOAST_ERROR_GENERIC'));
    return null;
  };

  const breadcrumbItems: Array<
    | { text: string }
    | {
        text: string;
        href: TRoutePathnames;
        navigationType: 'client-side' | 'external';
      }
  > = [
    {
      text: t('BREADCRUMB_ATTENDANCE'),
      href: '/org/[slug]/attendance/tracker/',
      navigationType: 'external',
    },
    { text: t('BREADCRUMB_MY_REQUESTS') },
  ];

  return (
    <PageLayout>
      <Helmet>
        <title>{t('SEO_TITLE')}</title>
        <meta name="description" content={t('SEO_DESCRIPTION')} />
      </Helmet>

      <Toaster />

      <PageLayoutTop>
        <PageLayoutBreadcrumb items={breadcrumbItems} />

        <PageHeaderWithActions
          title={t('PAGE_TITLE')}
          subtitle={t('PAGE_DESCRIPTION')}
          actions={
            isEmpty ? undefined : (
              <Button
                type="button"
                variant="primary"
                size="sm"
                startIcon={Plus}
                onClick={handleCreateClick}
              >
                {t('BUTTON_CREATE_REQUEST')}
              </Button>
            )
          }
        />
      </PageLayoutTop>

      <PageLayoutContent>
        {/* `useTableState` seeds its pagination once and has no effect that
            resynchronises it, so a page change decided OUTSIDE the table —
            cancelling the last row of a page sends the reader back to page
            1 — never reached the footer, which kept naming the old page.
            The key remounts the state on every page change. The table holds
            no other state, so nothing is lost. */}
        <RequestsTable
          key={`${page}-${pageSize}`}
          items={items}
          totalCount={totalCount}
          loading={loading}
          error={error}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          onRetry={refetch}
          onCreateClick={handleCreateClick}
          onRequestCancelled={handleRequestCancelled}
        />
      </PageLayoutContent>

      <CreateRequestDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreateSubmit}
        submitting={creating}
      />
    </PageLayout>
  );
};
